/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    CluCopyProjectSwagger,
    CluProjectDataSwagger,
    CluProjectImportFormat,
    CluProjectJobStatusSwagger,
    CluProjectSwagger,
    CluProjectType
} from 'src/clu/core/services/projects/cluProjectSwaggers';
import { createOperationCall, createOperationCallAndPoll, OperationHandler } from 'src/core/services/api/createOperationCall';
import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export const getCluProjectService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({ apiName: 'clu' });

    const apiOptions = (options?: { serviceKey?: ServiceKey; isMergePatchType?: boolean }) =>
        luisRequestOptionsBuilder()
            .useDefaultOptions(options?.serviceKey ?? context.serviceKey, isProduction)
            .useContentType(options?.isMergePatchType ? 'merge-patch+json' : 'json')
            .build();

    const getUrl = (projectName?: string, apiVersion?: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .queryParam('api-version', apiVersion ?? LuisConstants.Clu.AuthoringApiVersion);

    const getProjects = async (nextPageLink?: string, serviceKey?: ServiceKey): Promise<ResourceListResults<CluProjectSwagger>> => {
        const url = nextPageLink ?? getUrl().build();

        const { data } = await httpService.get<ResourceListResults<CluProjectSwagger>>(url, apiOptions({ serviceKey }));

        return data;
    };

    const getProject = async ({ projectName }: { projectName: string }): Promise<CluProjectSwagger> => {
        const url = getUrl(projectName).build();
        const { data } = await httpService.get<CluProjectSwagger>(url, apiOptions());

        return data;
    };

    const _exportProject = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName?: string;
    }): Promise<CluProjectDataSwagger> => {
        let urlBuild = getUrl(projectName)
            .resource(':export')
            .queryParam('format', 'Conversation')
            .queryParam('stringIndexType', 'Utf16CodeUnit');

        if (trainedModelName) {
            urlBuild = urlBuild.queryParam('trainedModelLabel', trainedModelName);
        }
        const url = urlBuild.build();

        const options = apiOptions();

        const { data } = await createOperationCallAndPoll<OperationStatusBaseSwagger & { resultUrl: string }>(
            httpService.post(url, {}, options),
            apiOptions(),
            undefined,
            response => (<Record<string, string>>response.headers)[LuisConstants.OperationHeaders.LocationHeader]
        );

        const { data: projectData } = await httpService.get<CluProjectDataSwagger>(data.resultUrl, apiOptions());

        return projectData;
    };

    const getProjectData = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName?: string;
    }): Promise<CluProjectDataSwagger> => await _exportProject({ projectName, trainedModelName });

    const createProject = async ({
        projectName,
        projectKind,
        language,
        description,
        multilingual,
        settings
    }: Pick<
        CluProjectSwagger,
        'projectName' | 'description' | 'multilingual' | 'language' | 'projectKind' | 'settings'
    >): Promise<CluProjectSwagger> => {
        const url = getUrl(projectName).build();

        const { data } = await httpService.patch<CluProjectSwagger>(
            url,
            { projectName, multilingual, description, projectKind, language, settings },
            apiOptions({ isMergePatchType: true })
        );

        return data;
    };

    const importProject = async ({
        projectName,
        fileContent,
        format
    }: {
        projectName: string;
        fileContent: string;
        format: CluProjectImportFormat;
    }) => {
        const url = getUrl(projectName)
            .resource(':import')
            .queryParam('format', format)
            .build();

        const options = apiOptions();

        await createOperationCallAndPoll<OperationStatusBaseSwagger>(
            fileService.upload<string>(url, fileContent, options),
            apiOptions(),
            undefined,
            response => (<Record<string, string>>response.headers)[LuisConstants.OperationHeaders.LocationHeader]
        );
    };

    const updateProject = async ({ projectName, ...projectDto }: { projectName: string } & Partial<CluProjectSwagger>): Promise<void> => {
        const url = getUrl(projectName).build();

        await httpService.patch<void>(url, projectDto, apiOptions({ isMergePatchType: true }));
    };

    const authorizeCopyProject = async ({
        serviceKey,
        resourceId,
        projectName,
        projectKind,
        allowOverwrite,
        targetResourceEndpoint
    }: {
        serviceKey: string;
        resourceId: string;
        projectName: string;
        allowOverwrite: boolean;
        projectKind: CluProjectType;
        targetResourceEndpoint: string;
    }): Promise<CluCopyProjectSwagger> => {
        const url = (isProduction
            ? urlBuilder()
                  .baseUrl(`${targetResourceEndpoint}/language/authoring/analyze-conversations`)
                  .project(projectName)
                  .queryParam('api-version', LuisConstants.Clu.AuthoringApiVersion)
            : getUrl(projectName, LuisConstants.Clu.AuthoringApiVersion)
        )
            .resource(':authorize-copy')
            .build();
        const resourceKey = isProduction ? serviceKey : resourceId;
        const modifiedApiOptions = luisRequestOptionsBuilder()
            .useAuthoringKey(resourceKey, isProduction, resourceId)
            .build();

        const response = await httpService.post<CluCopyProjectSwagger>(url, { projectKind, allowOverwrite }, modifiedApiOptions);

        return response.data;
    };

    const copyProject = async ({
        operationHandle,
        copyProjectSwagger
    }: {
        copyProjectSwagger: CluCopyProjectSwagger;
        operationHandle: OperationHandler<CluProjectJobStatusSwagger>;
    }): Promise<void> => {
        const url = getUrl(copyProjectSwagger.targetProjectName, LuisConstants.Clu.AuthoringApiVersion)
            .resource(':copy')
            .build();

        const { headers: copyJobHeaders } = await httpService.post<void>(url, copyProjectSwagger, apiOptions());

        const jobUrl = (<Record<string, string>>copyJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];

        await createOperationCall<CluProjectJobStatusSwagger>(jobUrl, apiOptions(), operationHandle);
    };

    const deleteProject = async ({
        projectName,
        operationHandle
    }: {
        projectName: string;
        operationHandle: OperationHandler<CluProjectJobStatusSwagger>;
    }): Promise<void> => {
        const url = getUrl(projectName).build();

        const { headers: deleteJobHeaders } = await httpService.delete<void>(url, apiOptions());

        const jobUrl = (<Record<string, string>>deleteJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];

        await createOperationCall<CluProjectJobStatusSwagger>(jobUrl, apiOptions(), operationHandle);
    };

    const exportProject = async ({ projectName, trainedModelName }: { projectName: string; trainedModelName?: string }): Promise<void> => {
        const projectData = await _exportProject({ projectName, trainedModelName });

        const fileName = trainedModelName ? `${projectName}(${trainedModelName}).json` : `${projectName}.json`;
        fileService.saveToDisk(JSON.stringify(projectData, null, 4), fileName);
    };

    return {
        getProject,
        copyProject,
        getProjects,
        createProject,
        exportProject,
        updateProject,
        deleteProject,
        importProject,
        getProjectData,
        authorizeCopyProject
    };
});
